import React, { useRef, useState } from "react";
import { OwcModalDialog } from "@one/react";
import ReactDOM from "react-dom";
import { Formik } from "formik";
import { THEME } from "@digitallab/grid-common-frontend-components";
import { RenderFormColumns } from "./FormComponents/RenderFormColumns";
import { FormFooterWithButtons } from "./FormComponents/FormFooterWithButtons";
import { generateValidationSchema } from "./generateValidationSchema";
import { IDialogProps } from "./IDialogPropsTypes";

/**
 * Service to show dialog with 2 buttons returning promise
 * To use:
 * 1. place dialog.body in your jsx code
 * 2. call dialog.show() to present dialog
 * you can call it with following params:
 * dialog.show({title?:ReactNode, body?:ReactNode, type?:"form", columns:Array<ColumnType>, confirmOnly?:boolean,
 *  confirmText="OK":ReactNode, cancelText="Cancel":ReactNode, confirmButtonVariant:Variant })
 * @returns
 * Promise that resolves to true or form values when confirmed, and rejected to false when Cancel clicked
 */
export function useDialogService() {
  const promiseRef = useRef<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
  }>(null);
  const [dialog, setDialog] = useState(<></>);

  const show = (props: IDialogProps) => {
    const {
      title,
      body,
      type,
      initialValues,
      columns = [],
      confirmOnly = false,
      confirmText = "OK",
      cancelText = "Cancel",
      confirmButtonVariant = "primary",
      ...rest
    } = props;

    setDialog(
      <>
        {ReactDOM.createPortal(
          <OwcModalDialog
            // @ts-ignore due to bug all CSS properties are required
            backdropProps={{ style: { zIndex: "10103" } }}
            visible
            onVisibleChange={(event: { detail: boolean }) => {
              if (event.detail === false) onClose("reject");
            }}
            onClick={(event: { stopPropagation: () => void }) => {
              event.stopPropagation();
            }}
            {...rest}
          >
            {title && <div slot="header">{title}</div>}
            <div
              style={{
                minHeight: THEME["one-sizer-base-100"],
                overflow: "hidden",
                padding: THEME["one-spacer-16"]
              }}
            >
              {type === "form" && columns && (
                <Formik
                  onSubmit={(values) => {
                    onClose(values);
                  }}
                  initialValues={initialValues || {}}
                  validationSchema={generateValidationSchema(columns)}
                  validateOnMount
                >
                  <>
                    <RenderFormColumns columns={columns} />
                    <FormFooterWithButtons
                      confirmOnly={confirmOnly}
                      cancelText={cancelText}
                      onClose={onClose}
                      confirmButtonVariant={confirmButtonVariant}
                      confirmText={confirmText}
                      type="form"
                    />
                  </>
                </Formik>
              )}
              {body && body}
              {type !== "form" && (
                <FormFooterWithButtons
                  confirmOnly={confirmOnly}
                  cancelText={cancelText}
                  onClose={onClose}
                  confirmButtonVariant={confirmButtonVariant}
                  confirmText={confirmText}
                />
              )}
            </div>
          </OwcModalDialog>,
          document.body
        )}
      </>
    );
    return new Promise((resolve, reject) => {
      promiseRef.current = { resolve, reject };
    });
  };

  function onClose(resultType: "resolve" | "reject" | Record<string, unknown>) {
    setDialog(<></>);
    if (promiseRef.current)
      resultType === "reject"
        ? promiseRef.current.reject(false)
        : promiseRef.current.resolve(resultType === "resolve" ? true : resultType);
  }

  return { show, dialogBody: dialog };
}
